import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FiArrowLeft,
  FiCalendar,
  FiLoader,
  FiRefreshCcw,
} from "react-icons/fi";
import axiosInstance from "../../../Utils/axiosInstance";
import { MONTHS, YEAR } from "./EDADashboardDetail";

const DEFAULT_MONTH = 3;
const DEFAULT_YEAR = 2026;

const PERIOD_OPTIONS = [
  {
    value: "current",
    label: "Current month (Mar)",
    months: [1],
    year: 2026,
  },
  {
    value: "last1",
    label: "Last 1 month (Feb - Mar)",
    months: [1,2],
    year: 2026,
  },
  {
    value: "last3",
    label: "Last 3 months (Jan - Mar)",
    months: [1, 2, 3],
    year: 2026,
  },
  {
    value: "last6",
    label: "Last 6 months (Oct - Mar)",
    months: [1, 2, 3,4,5,6],
    year: 2026,
  },
  {
    value: "last12",
    label: "Last 12 months (Apr - Mar)",
    months: [1, 2, 3,4, 5, 6, 7, 8, 9, 10, 11, 12],
    year: 2026,
  },
];

const businessPlanningTableMap = {
  factory: "VAYONA_FIXEDCOST1_FACTORY_REPORT",
  onshore: "VAYONA_FIXEDCOST1_ONSHORE_REPORT",
  service: "VAYONA_FIXEDCOST1_1_MAINSERVICE_REPORT",
};

const defaultBusinessPlanningSummary = {
  factory: [
    {
      "Category as per BP": "Facility Management Costs",
      MonthYear: "Mar-2026",
      Baseline_PerYear: 19.42,
      Target_PerYear: 19.42,
      Baseline_PerMonth: 1.62,
      Target_PerMonth: 1.62,
      ActualValue: -7.66,
    },
    {
      "Category as per BP": "Others",
      MonthYear: "Mar-2026",
      Baseline_PerYear: 9.32,
      Target_PerYear: 9.32,
      Baseline_PerMonth: 0.78,
      Target_PerMonth: 0.78,
      ActualValue: 0.74,
    },
    {
      "Category as per BP": "Insurance",
      MonthYear: "Mar-2026",
      Baseline_PerYear: 5.58,
      Target_PerYear: 5.58,
      Baseline_PerMonth: 0.47,
      Target_PerMonth: 0.47,
      ActualValue: 0.76,
    },
    {
      "Category as per BP": "Payroll",
      MonthYear: "Mar-2026",
      Baseline_PerYear: 0,
      Target_PerYear: 0,
      Baseline_PerMonth: 0,
      Target_PerMonth: 0,
      ActualValue: 6.46,
    },
    {
      "Category as per BP": "Warehouse operations & Consumables",
      MonthYear: "Mar-2026",
      Baseline_PerYear: 4.25,
      Target_PerYear: 4.25,
      Baseline_PerMonth: 0.35,
      Target_PerMonth: 0.35,
      ActualValue: 0.43,
    },
    {
      "Category as per BP": "Repair & Maintenance",
      MonthYear: "Mar-2026",
      Baseline_PerYear: 2.4,
      Target_PerYear: 2.4,
      Baseline_PerMonth: 0.2,
      Target_PerMonth: 0.2,
      ActualValue: 0.05,
    },
    {
      "Category as per BP": "Logistics & Intercrating, Cranes",
      MonthYear: "Mar-2026",
      Baseline_PerYear: 4.86,
      Target_PerYear: 4.86,
      Baseline_PerMonth: 0.41,
      Target_PerMonth: 0.41,
      ActualValue: 0,
    },
    {
      "Category as per BP": "Total",
      MonthYear: "Mar-2026",
      Baseline_PerYear: 45.83,
      Target_PerYear: 45.83,
      Baseline_PerMonth: 3.83,
      Target_PerMonth: 3.83,
      ActualValue: 0.15,
    },
  ],
  onshore: [
    {
      "Category as per BP": "Facility Management Costs",
      MonthYear: "Mar-2026",
      Baseline_PerYear: 4.4,
      Target_PerYear: 4.4,
      Baseline_PerMonth: 0.37,
      Target_PerMonth: 0.37,
      ActualValue: 316.56,
    },
    {
      "Category as per BP": "Insurance",
      MonthYear: "Mar-2026",
      Baseline_PerYear: 18.24,
      Target_PerYear: 4.74,
      Baseline_PerMonth: 1.52,
      Target_PerMonth: 0.4,
      ActualValue: 0.42,
    },
    {
      "Category as per BP": "Others",
      MonthYear: "Mar-2026",
      Baseline_PerYear: 22.61,
      Target_PerYear: 22.61,
      Baseline_PerMonth: 1.88,
      Target_PerMonth: 1.88,
      ActualValue: 1.66,
    },
    {
      "Category as per BP": "Payroll",
      MonthYear: "Mar-2026",
      Baseline_PerYear: 0,
      Target_PerYear: 0,
      Baseline_PerMonth: 0,
      Target_PerMonth: 0,
      ActualValue: 0,
    },
    {
      "Category as per BP": "Repair & Maintenance",
      MonthYear: "Mar-2026",
      Baseline_PerYear: 0.77,
      Target_PerYear: 0.77,
      Baseline_PerMonth: 0.06,
      Target_PerMonth: 0.06,
      ActualValue: 0.46,
    },
    {
      "Category as per BP": "Warehouse operations expenses & Consumables",
      MonthYear: "Mar-2026",
      Baseline_PerYear: 29.44,
      Target_PerYear: 5.44,
      Baseline_PerMonth: 2.45,
      Target_PerMonth: 0.45,
      ActualValue: -2.77,
    },
    {
      "Category as per BP": "Total",
      MonthYear: "Mar-2026",
      Baseline_PerYear: 75.46,
      Target_PerYear: 37.96,
      Baseline_PerMonth: 6.28,
      Target_PerMonth: 3.16,
      ActualValue: 316.33,
    },
  ],
  service: [],
};

const normalizeSegmentKey = (value = "") => {
  const normalizedValue = value.toLowerCase();

  if (normalizedValue.includes("factory")) {
    return "factory";
  }

  if (normalizedValue.includes("onshore")) {
    return "onshore";
  }

  if (normalizedValue.includes("service")) {
    return "service";
  }

  return "factory";
};

const formatSegmentTitle = (value = "") => {
  const segmentKey = normalizeSegmentKey(value);
  return segmentKey.charAt(0).toUpperCase() + segmentKey.slice(1);
};

const formatTableCellValue = (value, allowZeroAsDash = false) => {
  const parsedValue = Number(value);

  if (!Number.isFinite(parsedValue)) {
    return "-";
  }

  if (allowZeroAsDash && parsedValue === 0) {
    return "-";
  }

  return parsedValue
    .toFixed(2)
    .replace(/\.00$/, "")
    .replace(/(\.\d)0$/, "$1");
};

const EDASegmentDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    dashboard,
    selectedProcess,
    segment,
    month: initialMonth,
    year: initialYear,
  } = location.state || {};

  const segmentKey = normalizeSegmentKey(segment);
  const segmentTitle = formatSegmentTitle(segment);
  const [selectedPeriod, setSelectedPeriod] = useState(PERIOD_OPTIONS[0]);
  const [selectedYear, setSelectedYear] = useState(initialYear || DEFAULT_YEAR);
  const [rows, setRows] = useState(
     []
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [classificationData, setClassificationData] = useState([]);
  const [classificationLoading, setClassificationLoading] = useState(false);
  const [classificationError, setClassificationError] = useState("");

  const monthColumns = useMemo(() => {
  if (!rows.length) return [];

  const row = rows[0];

  return Object.keys(row)
    .filter((key) => key.endsWith("_Actual"))
    .map((key) => {
      const prefix = key.replace("_Actual", "");

      return {
        key,
        title: row[`${prefix}_MonthYear`] || prefix,
      };
    });
}, [rows]);

const transformedClassificationData = useMemo(() => {
  if (!classificationData.length) return [];

  const map = {};

  classificationData.forEach((monthObj) => {
    const month = monthObj.MonthYear;

    monthObj.Items.forEach((item) => {
      const key = item.Particulars;

      if (!map[key]) {
        map[key] = {
          Particulars: key
        };
      }

      Object.entries(item).forEach(([field, value]) => {
        if (
          field !== "Particulars" &&
          field !== "Category as per BP"
        ) {
          map[key][`${month}_${field}`] = value;
        }
      });
    });
  });

  return Object.values(map);
}, [classificationData]);

const classificationMonths = useMemo(
  () => classificationData.map(x => x.MonthYear),
  [classificationData]
);

const segmentColumns = useMemo(() => {
  if (!classificationData.length) return [];

  const firstItem = classificationData[0]?.Items?.[0];

  if (!firstItem) return [];

  return Object.keys(firstItem).filter(
    key =>
      key !== "Particulars" &&
      key !== "Category as per BP"
  );
}, [classificationData]);

  const handleRowClick = (category) => {
    if (selectedCategory === category) {
      setSelectedCategory(null);
    } else {
      setSelectedCategory(category);
    }
  };

  useEffect(() => {
    if (!location.state) {
      return;
    }

    let ignore = false;

    const fetchBusinessPlanningSummary = async () => {
      setLoading(true);
      setError("");

      try {
        const response = await axiosInstance.post(
          "/eda/business-planning-summary",
          {
            Months: selectedPeriod.months.join(","), // This seems to be an array of month numbers
            Year: selectedYear,
            TableName: businessPlanningTableMap[segmentKey],
          },
        );

        if (!ignore && response?.data?.status) {
          setRows(response?.data?.items || []);
        }
      } catch (apiError) {
        if (!ignore) {
          setError(
            apiError?.response?.data?.message ||
              "Failed to fetch business planning summary."
          );
          setRows([]);
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    fetchBusinessPlanningSummary();

    return () => {
      ignore = true;
    };
  }, [location.state, segmentKey, selectedPeriod, selectedYear]);

  useEffect(() => {
    if (!selectedCategory || !location.state) {
      setClassificationData([]);
      setClassificationError("");
      return;
    }

    let ignore = false;
    const fetchClassificationData = async () => {
      setClassificationLoading(true);
      setClassificationError("");
      setClassificationData([]);
      try {
        const response = await axiosInstance.post(
          "/eda/fixed-cost-classification",
          {
            Months: selectedPeriod.months.join(","),
            Year: selectedYear,
            Category: selectedCategory,
            Type: segmentKey.toUpperCase(),
          },
        );

        if (!ignore && response?.data?.status) {
          setClassificationData(response.data.items || []);
        } else if (!ignore) {
          setClassificationError(
            response?.data?.message || "Failed to fetch classification data."
          );
        }
      } catch (apiError) {
        if (!ignore) {
          setClassificationError(
            apiError?.response?.data?.message ||
              "Failed to fetch classification data."
          );
        }
      } finally {
        if (!ignore) {
          setClassificationLoading(false);
        }
      }
    };

    fetchClassificationData();

    return () => {
      ignore = true;
    };
  }, [selectedCategory, selectedPeriod, selectedYear, location.state, segmentKey]);

  if (!location.state) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
        Open this page from the dashboard cards so the selected segment context
        is available.
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-gradient-to-b from-slate-50 to-white p-4 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">
              {segmentTitle} - Fixed Cost Optimization
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              Process: {selectedProcess} | Status:{" "}
              {dashboard?.edaStatus || "Available"}
            </p>
          </div>

          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 self-start rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-800"
          >
            <FiArrowLeft className="h-4 w-4" />
            Back
          </button>
        </div>

        <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex flex-col gap-3 md:flex-row md:items-center">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-slate-400">Year</span>
              <select
                value={selectedYear}
                onChange={(event) =>
                  setSelectedYear(Number(event.target.value))
                }
                disabled={loading}
                className="min-w-[120px] rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 outline-none transition focus:border-blue-300 disabled:cursor-not-allowed disabled:bg-slate-50"
              >
                {YEAR.map((year) => (
                  <option key={year.value} value={year.value}>
                    {year.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-slate-400">Period</span>
              <select
                value={selectedPeriod.value}
                onChange={(e) =>
                  setSelectedPeriod(
                    PERIOD_OPTIONS.find(
                      (option) => option.value === e.target.value,
                    ),
                  )
                }
                disabled={loading}
                className="min-w-[180px] rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 outline-none transition focus:border-blue-300 disabled:cursor-not-allowed disabled:bg-slate-50"
              >
                {PERIOD_OPTIONS.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </div>

            <button
              type="button"
              onClick={() => {
                setSelectedPeriod(PERIOD_OPTIONS[0]);
                setSelectedYear(DEFAULT_YEAR);
              }}
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-500 shadow-sm transition hover:border-slate-300 hover:text-slate-700 disabled:cursor-not-allowed disabled:bg-slate-50"
            >
              <FiRefreshCcw className="h-3.5 w-3.5" />
              Reset
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {loading && (
              <div className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-medium text-blue-700">
                <FiLoader className="h-4 w-4 animate-spin" />
                Loading summary...
              </div>
            )}

            <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-medium text-emerald-700">
              <FiCalendar className="h-4 w-4" />
              Current period: {selectedPeriod.label} of {selectedYear}
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 px-4 py-4">
          <p className="text-sm font-medium text-slate-400">
            Home / Insights / EDA Dashboard Detail / {segmentTitle} - Fixed Cost
            Optimization
          </p>
          <h2 className="mt-2 text-lg font-semibold text-slate-800">
            Nature of cost by BP category - Monthly cumulative actuals (Rs Cr)
          </h2>
        </div>

        {error && (
          <div className="border-b border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">
            {error}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm text-slate-700">
            <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">
              <tr>
                <th className="px-4 py-3">#</th>
                <th className="px-4 py-3">BP Category (Nature of Cost)</th>
                <th className="px-4 py-3 text-right">Baseline/yr</th>
                <th className="px-4 py-3 text-right">Target/yr</th>
                <th className="px-4 py-3 text-right">Bl/mo</th>
                <th className="px-4 py-3 text-right">Tg/mo</th>
                {monthColumns.map((month) => (
                  <th key={month.key} className="px-4 py-3 text-right">
                    {month.title}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    className="px-4 py-8 text-center text-sm text-slate-400"
                  >
                    No summary rows available for this segment.
                  </td>
                </tr>
              ) : (
                rows.map((row, index) => {
                  const isTotalRow =
                    String(row?.["Category as per BP"] || "")
                      .trim()
                      .toLowerCase() === "total";
                  const actualValue = Number(row?.ActualValue || 0);

                  return (
                    <tr
                      key={`${row?.["Category as per BP"] || "row"}-${index}`}
                      onClick={() =>
                        !isTotalRow && handleRowClick(row["Category as per BP"])
                      }
                      className={`border-t border-slate-100 ${
                        isTotalRow
                          ? "bg-slate-50 font-semibold"
                          : "cursor-pointer transition-colors hover:bg-slate-50"
                      } ${
                        selectedCategory === row["Category as per BP"] &&
                        !isTotalRow
                          ? "bg-blue-50 hover:bg-blue-100"
                          : ""
                      }`}
                    >
                      <td className="px-4 py-3 text-slate-400">
                        {isTotalRow ? "" : index + 1}
                      </td>
                      <td className="px-4 py-3">
                        {isTotalRow
                          ? "Total Cost"
                          : row?.["Category as per BP"] || "-"}
                      </td>
                      <td className="px-4 py-3 text-right font-mono">
                        {formatTableCellValue(
                          row?.Baseline_PerYear,
                          !isTotalRow,
                        )}
                      </td>
                      <td className="px-4 py-3 text-right font-mono">
                        {formatTableCellValue(row?.Target_PerYear, !isTotalRow)}
                      </td>
                      <td className="px-4 py-3 text-right font-mono">
                        {formatTableCellValue(
                          row?.Baseline_PerMonth,
                          !isTotalRow,
                        )}
                      </td>
                      <td className="px-4 py-3 text-right font-mono">
                        {formatTableCellValue(
                          row?.Target_PerMonth,
                          !isTotalRow,
                        )}
                      </td>
                      {monthColumns.map((month) => {
                        const value = Number(row[month.key] || 0);

                        return (
                          <td
                            key={month.key}
                            className={`px-4 py-3 text-right font-mono ${
                              value > 0
                                ? "text-emerald-600"
                                : value < 0
                                  ? "text-rose-600"
                                  : "text-slate-500"
                            }`}
                          >
                            {formatTableCellValue(value)}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {selectedCategory && (
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 p-4">
            <h2 className="text-lg font-semibold text-slate-800">
              Cost Classification for: {selectedCategory}
            </h2>
          </div>

          {classificationLoading && (
            <div className="flex items-center justify-center gap-2 p-8 text-sm text-slate-500">
              <FiLoader className="h-5 w-5 animate-spin" />
              <span>Loading classification details...</span>
            </div>
          )}

          {classificationError && (
            <div className="m-4 rounded-lg border border-red-200 bg-red-100 p-3 text-sm text-red-600">
              {classificationError}
            </div>
          )}

          {!classificationLoading && !classificationError && (
            <div className="overflow-x-auto">
              {classificationData.length > 0 ? (
                <table className="min-w-full table-fixed border-collapse text-sm text-slate-700">
                  <thead className="bg-slate-50">
                    <tr>
                      <th rowSpan={2} className="w-16 text-center px-4 py-3">
                        #
                      </th>
                      <th rowSpan={2} className="w-80 px-4 py-3 text-left">
                        Particulars
                      </th>

                      {classificationMonths.map((month) => (
                        <th
                          key={month}
                          colSpan={segmentColumns.length}
                          className="text-center font-bold text-blue-600"
                        >
                          {month}
                        </th>
                      ))}
                    </tr>

                    <tr>
                      {classificationMonths.flatMap((month) =>
                        segmentColumns.map((col) => (
                          <th
                            key={`${month}-${col}`}
                            className="text-center font-semibold"
                          >
                            {col}
                          </th>
                        )),
                      )}
                    </tr>
                  </thead>

                  <tbody>
                    {transformedClassificationData.map((row, index) => {
                      const isTotal = row.Particulars.toLowerCase() === "total";

                      return (
                        <tr
                          key={index}
                          className={
                            isTotal ? "bg-slate-50 font-bold" : "border-t"
                          }
                        >
                          <td className="px-4 py-3">
                            {isTotal ? "" : index + 1}
                          </td>

                          <td className="px-4 py-3">{row.Particulars}</td>

                          {classificationMonths.map((month) =>
                            segmentColumns.map((col) => {
                              const value = row[`${month}_${col}`];

                              return (
                                <td
                                  key={`${month}-${col}`}
                                  className={`px-4 py-3 text-center font-mono ${
                                    value > 0
                                      ? "text-green-600"
                                      : value < 0
                                        ? "text-red-600"
                                        : "text-slate-400"
                                  }`}
                                >
                                  {formatTableCellValue(value)}
                                </td>
                              );
                            }),
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <div className="py-8 text-center text-sm text-slate-400">
                  No classification data available for {selectedCategory}.
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EDASegmentDetail;
